import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Upload, Button, Popconfirm, message, Spin, Modal, Input, Empty, Typography
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, LoadingOutlined,
  LeftOutlined, RightOutlined, CloseOutlined,
} from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import { sheepApi } from '../api';
import { SheepPhoto } from '../types';

const BACKEND = process.env.REACT_APP_API_URL || '';

interface Props {
  sheepId: number;
  readOnly?: boolean;
}

export default function PhotoGallery({ sheepId, readOnly = false }: Props) {
  const [photos, setPhotos] = useState<SheepPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });
  const [editCaption, setEditCaption] = useState<{ id: number; value: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fn = readOnly ? sheepApi.getPublicPhotos : sheepApi.getPhotos;
      const r = await fn(sheepId);
      setPhotos(r.data);
    } finally {
      setLoading(false);
    }
  }, [sheepId, readOnly]);

  useEffect(() => { load(); }, [load]);

  // Keyboard navigation in lightbox
  useEffect(() => {
    if (!lightbox.open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setLightbox((l) => ({ ...l, index: Math.min(l.index + 1, photos.length - 1) }));
      if (e.key === 'ArrowLeft') setLightbox((l) => ({ ...l, index: Math.max(l.index - 1, 0) }));
      if (e.key === 'Escape') setLightbox({ open: false, index: 0 });
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox.open, photos.length]);

  const handleUpload = async (file: RcFile) => {
    if (!file.type.startsWith('image/')) { message.error('Только изображения'); return false; }
    if (file.size > 5 * 1024 * 1024) { message.error('Максимум 5 МБ'); return false; }
    setUploading(true);
    try {
      await sheepApi.addPhoto(sheepId, file);
      await load();
      message.success('Фото добавлено');
    } catch {
      message.error('Ошибка загрузки');
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleDelete = async (id: number) => {
    await sheepApi.deletePhoto(id);
    setPhotos((p) => p.filter((x) => x.id !== id));
    if (lightbox.open) setLightbox({ open: false, index: 0 });
    message.success('Фото удалено');
  };

  const handleSaveCaption = async () => {
    if (!editCaption) return;
    await sheepApi.updateCaption(editCaption.id, editCaption.value);
    setPhotos((p) => p.map((x) => x.id === editCaption.id ? { ...x, caption: editCaption.value } : x));
    setEditCaption(null);
    message.success('Подпись сохранена');
  };

  const current = photos[lightbox.index];

  if (loading) return <Spin style={{ display: 'block', margin: '24px auto' }} />;

  return (
    <div>
      {/* Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: 10,
      }}>
        {photos.map((photo, idx) => (
          <div key={photo.id} style={{ position: 'relative', group: 'photo' } as any}>
            <div
              onClick={() => setLightbox({ open: true, index: idx })}
              style={{
                width: '100%', aspectRatio: '1/1', overflow: 'hidden',
                borderRadius: 8, cursor: 'zoom-in', background: '#f0f0f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <img
                src={`${BACKEND}${photo.url}`}
                alt={photo.caption || ''}
                style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.2s' }}
                onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              />
            </div>
            {photo.caption && (
              <Typography.Text
                type="secondary"
                style={{ display: 'block', fontSize: 11, textAlign: 'center', marginTop: 3 }}
                ellipsis
              >
                {photo.caption}
              </Typography.Text>
            )}
            {!readOnly && (
              <div style={{
                position: 'absolute', top: 4, right: 4,
                display: 'flex', gap: 3,
              }}>
                <Button
                  size="small" type="primary" ghost
                  icon={<EditOutlined />}
                  style={{ padding: '0 5px', opacity: 0.85 }}
                  onClick={(e) => { e.stopPropagation(); setEditCaption({ id: photo.id, value: photo.caption || '' }); }}
                />
                <Popconfirm title="Удалить фото?" onConfirm={() => handleDelete(photo.id)}>
                  <Button
                    size="small" danger
                    icon={<DeleteOutlined />}
                    style={{ padding: '0 5px', opacity: 0.85 }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Popconfirm>
              </div>
            )}
          </div>
        ))}

        {/* Upload tile */}
        {!readOnly && (
          <Upload showUploadList={false} beforeUpload={handleUpload} accept="image/*">
            <div style={{
              aspectRatio: '1/1', borderRadius: 8, border: '2px dashed #d9d9d9',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', color: '#aaa',
              minHeight: 140, transition: 'border-color 0.2s, color 0.2s',
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#764ba2';
                e.currentTarget.style.color = '#764ba2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#d9d9d9';
                e.currentTarget.style.color = '#aaa';
              }}
            >
              {uploading
                ? <Spin indicator={<LoadingOutlined style={{ fontSize: 28 }} />} />
                : <><PlusOutlined style={{ fontSize: 28 }} /><span style={{ fontSize: 12, marginTop: 6 }}>Добавить фото</span></>
              }
            </div>
          </Upload>
        )}
      </div>

      {photos.length === 0 && readOnly && (
        <Empty description="Фотографий нет" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}

      {/* Lightbox */}
      <Modal
        open={lightbox.open}
        footer={null}
        onCancel={() => setLightbox({ open: false, index: 0 })}
        centered
        width="90vw"
        styles={{ body: { padding: 0, background: '#000', borderRadius: 8 } }}
        closeIcon={<CloseOutlined style={{ color: '#fff' }} />}
      >
        {current && (
          <div style={{ position: 'relative', background: '#000', borderRadius: 8 }}>
            <img
              src={`${BACKEND}${current.url}`}
              alt={current.caption || ''}
              style={{
                width: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block', borderRadius: 8,
              }}
            />
            {/* Nav arrows */}
            {lightbox.index > 0 && (
              <Button
                shape="circle" icon={<LeftOutlined />}
                onClick={() => setLightbox((l) => ({ ...l, index: l.index - 1 }))}
                style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', opacity: 0.8 }}
              />
            )}
            {lightbox.index < photos.length - 1 && (
              <Button
                shape="circle" icon={<RightOutlined />}
                onClick={() => setLightbox((l) => ({ ...l, index: l.index + 1 }))}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', opacity: 0.8 }}
              />
            )}
            {/* Caption + controls */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
              padding: '20px 16px 12px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderRadius: '0 0 8px 8px',
            }}>
              <Typography.Text style={{ color: '#fff', fontSize: 13 }}>
                {current.caption || ''}
              </Typography.Text>
              <Typography.Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                {lightbox.index + 1} / {photos.length}
              </Typography.Text>
            </div>
            {!readOnly && (
              <div style={{ position: 'absolute', top: 8, right: 44, display: 'flex', gap: 6 }}>
                <Button
                  size="small" icon={<EditOutlined />} ghost
                  onClick={() => setEditCaption({ id: current.id, value: current.caption || '' })}
                >
                  Подпись
                </Button>
                <Popconfirm title="Удалить фото?" onConfirm={() => handleDelete(current.id)}>
                  <Button size="small" danger icon={<DeleteOutlined />}>Удалить</Button>
                </Popconfirm>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Edit caption modal */}
      <Modal
        title="Редактировать подпись"
        open={!!editCaption}
        onOk={handleSaveCaption}
        onCancel={() => setEditCaption(null)}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Input
          value={editCaption?.value}
          onChange={(e) => setEditCaption((c) => c ? { ...c, value: e.target.value } : null)}
          placeholder="Подпись к фото..."
          onPressEnter={handleSaveCaption}
          autoFocus
        />
      </Modal>
    </div>
  );
}
